import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";

const PreviewState = ({ message, tone = "muted" }) => {
  const toneClass =
    tone === "error" ? "text-destructive" : "text-muted-foreground";

  return (
    <div className="flex min-h-56 items-center justify-center rounded-radius border border-dashed border-border bg-muted/10 px-4 py-10 text-sm">
      <span className={toneClass}>{message}</span>
    </div>
  );
};

const PreviewSection = ({ section, isFirst = false }) => (
  <section
    id={section.id}
    className={[
      "space-y-3 scroll-mt-4",
      !isFirst ? "border-t border-slate-200 pt-8" : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    {section.title ? (
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-[0.24em] text-slate-500">
          {section.title}
        </p>
        {section.description ? (
          <p className="text-sm text-slate-500">{section.description}</p>
        ) : null}
      </div>
    ) : null}

    {section.html ? (
      <div
        className="preview-html space-y-3 text-sm text-slate-900"
        dangerouslySetInnerHTML={{ __html: section.html }}
      />
    ) : null}
  </section>
);

const PreviewContent = ({ content, loading, error, emptyMessage }) => {
  const { t } = useTran();
  const resolvedEmptyMessage = emptyMessage ?? t("No preview data available.");

  if (loading) {
    return <PreviewState message={t("Loading preview data...")} />;
  }

  if (error) {
    return <PreviewState message={error} tone="error" />;
  }

  if (!content?.sections?.length) {
    return <PreviewState message={resolvedEmptyMessage} />;
  }

  const navigationItems = Array.isArray(content.navigationItems)
    ? content.navigationItems
    : content.sections
        .filter((section) => section?.id && section?.title)
        .map((section) => ({ id: section.id, label: section.title }));

  return (
    <div className="flex justify-center bg-background px-2 py-2 sm:px-4">
      <div className="w-full max-w-[1120px] rounded-radius border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
        {content.cover ? (
          <div className="border-b border-slate-200 px-8 py-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                {content.cover.eyebrow ? (
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-500">
                    {content.cover.eyebrow}
                  </p>
                ) : null}
                <h2 className="text-2xl font-semibold text-slate-900">
                  {content.cover.title || content.title}
                </h2>
                {content.cover.description || content.description ? (
                  <p className="text-sm text-slate-500">
                    {content.cover.description || content.description}
                  </p>
                ) : null}
              </div>

              {content.cover.meta?.length ? (
                <div className="min-w-[220px] rounded-radius border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  {content.cover.meta.map((item) => (
                    <div
                      key={`${item.label}-${item.value}`}
                      className="space-y-1 not-first:mt-3"
                    >
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        {item.label}
                      </p>
                      <p className="text-slate-900">{item.value || "-"}</p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {content.coverHtml ? (
          <div
            className="border-b border-slate-200"
            dangerouslySetInnerHTML={{ __html: content.coverHtml }}
          />
        ) : null}

        <div className="space-y-6 px-8 py-6">
          {navigationItems.length > 0 && content.showNavigation !== false ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-[0.24em] text-slate-500">
                {t("Contents")}
              </span>
              {navigationItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-900 transition hover:bg-slate-50"
                >
                  {item.label}
                </a>
              ))}
              {content.badge ? (
                <Badge
                  variant="outline"
                  className="border-slate-200 bg-white text-slate-700"
                >
                  {content.badge}
                </Badge>
              ) : null}
            </div>
          ) : null}

          {content.sections.map((section, index) => (
            <PreviewSection
              key={section.id || section.title}
              section={section}
              isFirst={!content.cover && !content.coverHtml && index === 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreviewContent;
